/*
 * ____   ____    .__   __
 * \   \ /   /___ |  |_/  |______      ____ ___.__.
 *  \   Y   /  _ \|  |\   __  \_ \   / ___<   |  |
 *   \     (  <_> )  |_|  |  / __ \_/ /_/  >___  |
 *    \___/ \____/|____/__| (____  /\___  // ____|
 *                               \//_____/ \/
 *
 * Copyright (c) 2017-2025 Voltagy. All rights reserved.
 * https://voltagy.com
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the proprietary and confidential property of Voltagy.
 * Unauthorized copying, transfer, or use of this file, via any medium, is
 * strictly prohibited without the express written permission of Voltagy.
 *
 * The receipt or possession of this source code and/or related information
 * does not convey or imply any right to use it, reproduce it, disclose its
 * contents, or to manufacture, use, or sell anything that it may describe.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
 * ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE
 * LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR
 * CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF
 * SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN
 * CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 * ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 *
 */

import React from "react";
import { withTranslation } from "react-i18next";

import ErrorScreen from "../app/routes/ErrorScreen";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.logError(error, errorInfo);
  }

  componentDidMount() {
    window.addEventListener("error", this.handleGlobalError);
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  componentWillUnmount() {
    window.removeEventListener("error", this.handleGlobalError);
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  getErrorMessage = (error) => {
    const { t } = this.props;
    switch (error.name) {
      case "AbortError":
        return t("errorBoundary.abortError");
      case "EvalError":
        return t("errorBoundary.evalError");
      case "InternalError":
        return t("errorBoundary.internalError");
      case "InvalidStateError":
        return t("errorBoundary.invalidStateError");
      case "NetworkError":
        return t("errorBoundary.networkError");
      case "NotFoundError":
        return t("errorBoundary.notFoundError");
      case "PermissionDeniedError":
        return t("errorBoundary.permissionDeniedError");
      case "QuotaExceededError":
        return t("errorBoundary.quotaExceededError");
      case "RangeError":
        return t("errorBoundary.rangeError");
      case "ReferenceError":
        return t("errorBoundary.referenceError");
      case "SecurityError":
        return t("errorBoundary.securityError");
      case "SyntaxError":
        return t("errorBoundary.syntaxError");
      case "TimeoutError":
        return t("errorBoundary.timeoutError");
      case "TypeError":
        return t("errorBoundary.typeError");
      case "URIError":
        return t("errorBoundary.uriError");
      default:
        if (error.message && error.message.includes("Loading chunk")) {
          return t("errorBoundary.failedToLoadPart");
        }
        if (error.message && error.message.includes("Failed to fetch")) {
          return t("errorBoundary.failedToFetchData");
        }
        return t("errorBoundary.unexpectedError");
    }
  };

  handleGlobalError = (event) => {
    event.preventDefault();
    this.logError(event.error || new Error(event.message));
  };

  handleUnhandledRejection = (event) => {
    event.preventDefault();
    this.logError(
      event.reason ||
        new Error(this.props.t("errorBoundary.unhandledRejection"))
    );
  };

  logError = (error, errorInfo = null) => {
    console.error(this.props.t("errorBoundary.loggedError"), error, errorInfo);

    this.setState({
      hasError: true,
      error: {
        message: this.getErrorMessage(error),
        details: error.toString(),
      },
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorScreen darkMode={this.props.darkMode} error={this.state.error} />
      );
    }

    return this.props.children;
  }
}

export default withTranslation(["base", "voltagy"])(ErrorBoundary);
